import React, { useState, useCallback, Suspense, lazy } from 'react';
import './App.css';
import Nav from './components/Nav';
import Hero from './components/Hero';
import Projects from './components/Projects';
import Skills from './components/Skills';
import About from './components/About';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Overview from './components/Overview';
import WebApps from './components/WebApps';
import CardPage from './CardPage';
import QRPage from './QRPage';

// Heavy tool pages loaded lazily -- their dependencies (pdfjs, tesseract,
// mammoth, jspdf) are only downloaded when the user actually visits that route.
// This keeps the main bundle small for portfolio visitors.
const ARTypingPage    = lazy(() => import('./ARTypingPage'));
const ConverterPage   = lazy(() => import('./ConverterPage'));
const GalleryPage     = lazy(() => import('./GalleryPage'));
const PhoneWordsPage  = lazy(() => import('./PhoneWordsPage'));

// Error boundary -- catches crashes in tool pages without taking down the portfolio
class ErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) return (
      <div style={{ padding: '3rem', textAlign: 'center', fontFamily: 'sans-serif' }}>
        <h2 style={{ marginBottom: '1rem' }}>Something went wrong</h2>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>{this.state.error.message}</p>
        <a href="/" style={{ color: '#1A2744' }}>← Return to portfolio</a>
      </div>
    );
    return this.props.children;
  }
}

// Loading fallback shown while lazy bundle downloads
function PageLoader() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      minHeight:'100vh', fontFamily:'sans-serif', color:'#6B6355', fontSize:'0.85rem',
      letterSpacing:'0.1em', textTransform:'uppercase' }}>
      Loading…
    </div>
  );
}

function getRoute() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('card'))        return 'card';
  if (params.has('qr'))          return 'qr';
  if (params.has('artyping'))    return 'artyping';
  if (params.has('converter'))   return 'converter';
  if (params.has('gallery'))     return 'gallery';
  if (params.has('phonewords'))  return 'phonewords';
  return 'portfolio';
}

function App() {
  const route = getRoute();
  const [overviewOpen, setOverviewOpen] = useState(false);
  const openOverview  = useCallback(() => setOverviewOpen(true),  []);
  const closeOverview = useCallback(() => setOverviewOpen(false), []);
  const [webAppsOpen, setWebAppsOpen] = useState(false);
  const openWebApps  = useCallback(() => setWebAppsOpen(true),  []);
  const closeWebApps = useCallback(() => setWebAppsOpen(false), []);

  if (route === 'card')  return <CardPage />;
  if (route === 'qr')    return <QRPage />;

  if (route === 'artyping') return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <ARTypingPage />
      </Suspense>
    </ErrorBoundary>
  );

  if (route === 'converter') return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <ConverterPage />
      </Suspense>
    </ErrorBoundary>
  );

  if (route === 'gallery') return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <GalleryPage />
      </Suspense>
    </ErrorBoundary>
  );

  if (route === 'phonewords') return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <PhoneWordsPage />
      </Suspense>
    </ErrorBoundary>
  );

  return (
    <div className="app">
      <Overview isOpen={overviewOpen} onClose={closeOverview} />
      <WebApps isOpen={webAppsOpen} onClose={closeWebApps} />
      <Nav onOverviewOpen={openOverview} onWebAppsOpen={openWebApps} />
      <Hero onOverviewOpen={openOverview} />
      <Projects onWebAppsOpen={openWebApps} />
      <Skills />
      <About />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;